import React from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Megaphone, ChevronDown, ChevronUp, Hash, Layers, Loader2, Lock, MessageSquare, Pencil,
  Pin, Plus, Settings2, Users,
} from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { timeAgo } from "@/lib/timeago";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ChatterFeed } from "@/components/social/ChatterFeed";
import { useAuth } from "@/context/AuthContext";
import { GroupMessageThread } from "@/components/groups/GroupMessageThread";
import { GroupSearchResults } from "@/components/groups/GroupSearchResults";
import { GroupNewMessageDialog } from "@/components/groups/GroupNewMessageDialog";
import { STICKY_RAIL, scrollSectionIntoView } from "@/components/groups/groupSticky";

const ALL = "__all__";
// Announcements is an INDEX, not a Stream (GRP-DEC-333). It is a sibling of All Streams in
// the rail and deliberately takes no part in Stream unread-cursor semantics.
const ANN = "__announcements__";
// Group Member Messaging V1 — a private conversation is selected in the SAME rail and
// replaces the Stream feed in the SAME centre column, so the member never leaves the
// Group workspace (messaging spec §13/§15). The prefix keeps thread ids from ever
// colliding with a Stream id.
const MSG = "mm:";
const isThread = (sel) => typeof sel === "string" && sel.startsWith(MSG);
const threadOf = (sel) => (isThread(sel) ? sel.slice(MSG.length) : null);
const keyOf = (i) => `${i.identity_type}:${i.identity_id}`;
// One line of the post's own text — the strip REFERENCES the canonical post, it does not
// re-render it, so there is exactly one copy of a pinned message on the page.
const EXCERPT = (html) => String(html || "")
  .replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();

/**
 * Batch C.1 — the pinned strip (GRP-DEC-163).
 *
 * Collapsible, and rendered ONLY when the Group actually has pins, so an unpinned Group
 * pays no vertical space for it. The cap ("2 of 3 pinned") and Unpin are organizer-only:
 * an ordinary member sees the pinned messages and nothing to manage.
 */
function PinnedStrip({ groupId, pins, maxPins, canManage, onOpen, onChanged }) {
  const [open, setOpen] = React.useState(true);
  if (!pins.length) return null;
  const unpin = async (postId) => {
    try {
      await api.post(`/groups/${groupId}/posts/${postId}/unpin`, {});
      toast.success("Unpinned from group");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not unpin that post.");
    }
  };
  return (
    <Card className="min-w-0 overflow-hidden border-0 p-0 shadow-md ring-1 ring-border"
          data-testid="group-pinned-strip">
      <button type="button" onClick={() => setOpen((v) => !v)}
              aria-expanded={open} aria-controls="group-pinned-list"
              data-testid="group-pinned-toggle"
              className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-secondary/60">
        <Pin className="h-4 w-4 shrink-0 text-teal-700" aria-hidden="true" />
        <span className="text-sm font-[600]">Pinned</span>
        {canManage ? (
          <span className="truncate text-xs text-muted-foreground"
                data-testid="group-pinned-cap">
            {pins.length} of {maxPins} pinned
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">{pins.length}</span>
        )}
        {open ? <ChevronUp className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" />
          : <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" />}
      </button>
      {open ? (
        <ul id="group-pinned-list" className="divide-y divide-border border-t border-border">
          {pins.map((p) => (
            <li key={p.id} className="flex items-center gap-2 px-3 py-2"
                data-testid={`group-pinned-item-${p.id}`}>
              <button type="button" onClick={() => onOpen(p)}
                      className="min-w-0 flex-1 text-left"
                      data-testid={`group-pinned-open-${p.id}`}>
                <span className="block truncate text-sm">
                  {EXCERPT(p.body) || "(no text)"}
                </span>
                <span className="block truncate text-xs text-muted-foreground">
                  {(p.author || {}).display_name}
                  {p.group_context?.stream_name ? ` · ${p.group_context.stream_name}` : ""}
                </span>
              </button>
              {canManage ? (
                <Button size="sm" variant="ghost" className="h-7 shrink-0 px-2 text-xs"
                        onClick={() => unpin(p.id)}
                        data-testid={`group-pinned-unpin-${p.id}`}>
                  Unpin
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </Card>
  );
}

function CreateStreamDialog({ groupId, open, onOpenChange, onCreated }) {
  const [name, setName] = React.useState("");
  const [permission, setPermission] = React.useState("all");
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { if (open) { setName(""); setPermission("all"); } }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="stream-create-dialog">
        <DialogHeader>
          <DialogTitle>New stream</DialogTitle>
          <DialogDescription>
            A stream is a channel inside this group. Up to 50 active streams.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input value={name} maxLength={80} placeholder="Deal flow"
                   onChange={(e) => setName(e.target.value)}
                   data-testid="stream-create-name" />
          </div>
          <div className="space-y-1.5">
            <Label>Who can post</Label>
            <Select value={permission} onValueChange={setPermission}>
              <SelectTrigger data-testid="stream-create-permission"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All members</SelectItem>
                <SelectItem value="admins">Organizers only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button disabled={busy || !name.trim()} data-testid="stream-create-submit"
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await api.post(`/groups/${groupId}/streams`,
                        { name, post_permission: permission });
                      toast.success("Stream created.");
                      onOpenChange(false);
                      onCreated?.();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Couldn't create that stream.");
                    } finally { setBusy(false); }
                  }}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Create stream
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Rename a Stream. The default Stream may be renamed and stays the default underneath. */
function RenameStreamDialog({ groupId, stream, onOpenChange, onSaved }) {
  const [name, setName] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { if (stream) setName(stream.name || ""); }, [stream]);
  return (
    <Dialog open={Boolean(stream)} onOpenChange={onOpenChange}>
      <DialogContent data-testid="stream-rename-dialog">
        <DialogHeader>
          <DialogTitle>Rename stream</DialogTitle>
          <DialogDescription>Stream names are unique inside a group.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label>Name</Label>
          <Input value={name} maxLength={80} onChange={(e) => setName(e.target.value)}
                 data-testid="stream-rename-name" />
        </div>
        <DialogFooter>
          <Button disabled={busy || !name.trim()} data-testid="stream-rename-submit"
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await api.put(`/groups/${groupId}/streams/${stream.id}`, { name });
                      toast.success("Stream renamed.");
                      onOpenChange(false);
                      onSaved?.();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Couldn't rename that stream.");
                    } finally { setBusy(false); }
                  }}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Groups Phase 4 — the Group's Chatter experience: a Stream rail plus the ONE shared
 * ChatterFeed. All Streams is the default landing feed (GRP-DEC-143) and the composer
 * always targets a real Stream, defaulting to the Group Stream (GRP-DEC-144).
 *
 * The rail is a vertical list beside the feed from `lg` up and a horizontally scrollable
 * chip row on phones, so Stream navigation never pushes the page sideways.
 */
/**
 * Batch C.1 — the Announcements INDEX (GRP-DEC-333).
 *
 * A Group-wide list over the canonical Announcement posts, not a second Announcement
 * object and not a Stream: every entry names the Stream it lives in, and opening one
 * navigates to that Stream and brings the canonical post into view (GRP-DEC-334).
 */
function AnnouncementsIndex({ groupId, onOpen }) {
  const [page, setPage] = React.useState(1);
  const { data, isLoading } = useQuery({
    queryKey: ["group-announcements", groupId, page],
    queryFn: async () => (await api.get(`/groups/${groupId}/announcements`,
      { params: { page, limit: 20 } })).data,
  });
  const rows = data?.announcements || [];
  if (isLoading) {
    return <Card className="border-0 p-6" data-testid="group-announcements-index">
      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
    </Card>;
  }
  if (!rows.length) {
    return (
      <Card className="border-0 p-6 text-center" data-testid="group-announcements-index">
        <p className="text-sm text-muted-foreground">
          No announcements yet. Organizers can mark a message as an Announcement when they
          post it.
        </p>
      </Card>
    );
  }
  return (
    <Card className="min-w-0 overflow-hidden border-0 p-0"
          data-testid="group-announcements-index">
      <ul className="divide-y divide-border">
        {rows.map((a) => (
          <li key={a.id}>
            <button type="button" onClick={() => onOpen(a)}
                    className="block w-full px-4 py-3 text-left hover:bg-secondary/60"
                    data-testid={`group-announcement-open-${a.id}`}>
              <span className="flex flex-wrap items-center gap-1.5">
                <Badge className="gap-1">
                  <Megaphone className="h-3 w-3" aria-hidden="true" />Announcement
                </Badge>
                {a.notified_everyone ? (
                  <Badge variant="secondary"
                         data-testid={`group-announcement-everyone-${a.id}`}>@everyone</Badge>
                ) : null}
              </span>
              <span className="mt-1.5 block line-clamp-2 text-sm text-foreground">
                {EXCERPT(a.body) || "(no text)"}
              </span>
              <span className="mt-1 block truncate text-xs text-muted-foreground">
                {(a.author || {}).display_name}
                {a.group_context?.stream_name
                  ? <> · <span className="font-[560] text-foreground/80"
                              data-testid={`group-announcement-stream-${a.id}`}>
                      {a.group_context.stream_name}
                    </span></>
                  : null}
                {" · "}{timeAgo(a.created_at)}
              </span>
            </button>
          </li>
        ))}
      </ul>
      {data?.has_more || page > 1 ? (
        <div className="flex items-center justify-between gap-2 border-t border-border px-4 py-2">
          <Button size="sm" variant="ghost" disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  data-testid="group-announcements-prev">Newer</Button>
          <span className="text-xs text-muted-foreground">Page {page}</span>
          <Button size="sm" variant="ghost" disabled={!data?.has_more}
                  onClick={() => setPage((p) => p + 1)}
                  data-testid="group-announcements-next">Older</Button>
        </div>
      ) : null}
    </Card>
  );
}

export function GroupChatterPanel({ group, viewer, profileId, initialThreadId,
                                    onThreadOpened, searchQuery = "", onClearSearch }) {
  const qc = useQueryClient();
  const { user: authUser } = useAuth();
  const [selected, setSelected] = React.useState(
    initialThreadId ? `${MSG}${initialThreadId}` : ALL);
  const [composing, setComposing] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const [identityKey, setIdentityKey] = React.useState("");
  const [renaming, setRenaming] = React.useState(null);
  const [showArchived, setShowArchived] = React.useState(false);
  // Bumped when a pin changes, so the feed remounts and its own Pinned badges are correct.
  const [feedKey, setFeedKey] = React.useState(0);
  // Deep-link target inside the selected Stream (set by the Announcements index, by
  // in-column search, or by `?post=` on the URL — which is how a mention, comment or
  // announcement NOTIFICATION opens the post it is about). All Streams + `focus_post_id`
  // gives the same bounded window, so the reader never needs to know which Stream it was in.
  const [urlParams] = useSearchParams();
  const navKey = useLocation().key;
  const deepLinkPost = urlParams.get("post");
  const [focusPost, setFocusPost] = React.useState(deepLinkPost || null);
  // Reading `?post=` once at mount only works when the reader ARRIVES from elsewhere. When
  // this panel is already mounted — same Group, or the very same post again — nothing about
  // the component tree changes, which is why those clicks used to do nothing. Re-reading the
  // param on every navigation fixes a different post; the nonce is what re-fires an
  // IDENTICAL one, since React Router hands each click a fresh `location.key`.
  const [focusNonce, setFocusNonce] = React.useState(0);
  React.useEffect(() => {
    if (!deepLinkPost) return;
    setFocusPost(deepLinkPost);
    setFocusNonce((n) => n + 1);
    // All Streams, because the target can live in a Stream the reader isn't looking at —
    // a Stream-filtered feed would simply not contain it.
    setSelected(ALL);
  }, [deepLinkPost, navKey]);

  const { data } = useQuery({
    queryKey: ["group-streams", group.id, showArchived],
    queryFn: async () => (await api.get(`/groups/${group.id}/streams`, {
      params: showArchived ? { include_archived: true } : {},
    })).data,
  });
  // Phase 5 — the identities this member may post as INSIDE this Group: themselves, plus
  // this Group when they are an Owner/Admin. The server decides; the client only asks.
  const { data: identities } = useQuery({
    queryKey: ["group-post-identities", group.id],
    queryFn: async () => (await api.get("/chatter/identities",
      { params: { group_id: group.id } })).data,
  });
  const identityOpts = identities || [];
  const groupIdentity = identityOpts.find((i) => i.identity_type === "group") || null;
  const activeIdentity = identityOpts.find((i) => keyOf(i) === identityKey)
    || identityOpts.find((i) => i.identity_type === "personal")
    || identityOpts[0] || null;

  const streams = data?.streams || [];
  const defaultId = data?.default_stream_id;
  const canManage = Boolean(data?.can_manage);
  const refresh = () => qc.invalidateQueries({ queryKey: ["group-streams", group.id] });

  // Batch C.1 — pins are STREAM-scoped (GRP-DEC-331), so the strip is read for the Stream
  // the reader is actually in. All Streams is an aggregate view, not a Stream: it has no
  // pinned area, and the query is simply not made there.
  const pinStreamId = selected === ALL || selected === ANN || isThread(selected)
    ? null : selected;
  const pinsQ = useQuery({
    queryKey: ["group-pins", group.id, pinStreamId],
    queryFn: async () => (await api.get(`/groups/${group.id}/pins`,
      { params: { stream_id: pinStreamId } })).data,
    enabled: Boolean(pinStreamId),
  });
  const pins = pinStreamId ? (pinsQ.data?.posts || []) : [];
  // From the post's own menu the card already flips its badge locally, so only the strip
  // needs refreshing. From the strip there is no card to flip, so the feed is remounted.
  const pinsChanged = () => qc.invalidateQueries({ queryKey: ["group-pins", group.id] });
  const pinsChangedFromStrip = () => { pinsChanged(); setFeedKey((n) => n + 1); };
  // Opening a pinned message scrolls to the ONE canonical post card. A pin always belongs
  // to the Stream being viewed, so no Stream switch is needed here.
  const openPinned = (p) => {
    let tries = 0;
    const go = () => {
      const el = document.getElementById(`post-${p.id}`);
      if (el) {
        // `block: "start"` so the card's `scroll-margin-top` (which includes the sticky
        // strip's measured height) is what decides where it lands.
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        el.classList.add("so-flash");
        setTimeout(() => el.classList.remove("so-flash"), 1600);
        return;
      }
      if (tries++ < 15) setTimeout(go, 150);
      else setFocusPost(p.id);        // further back than the loaded page — deep-link it
    };
    setTimeout(go, 120);
  };
  // GRP-DEC-334 — an Announcement opens its OWN Stream at its OWN post. The post id travels
  // to the feed as a focus cursor, so the server returns a bounded window around it instead
  // of the client paging down through the whole Stream.
  const openAnnouncement = (a) => {
    const sid = a.group_context?.stream_id;
    setSelected(sid || ALL);
    setFocusPost(a.id);
  };
  // Selecting ANYTHING in the rail — All Streams, Announcements, a Stream or a private
  // conversation — brings the top of the centre column into view under the nav and tab
  // bar. Without this, picking a section while scrolled deep into a long feed silently
  // left the new content somewhere below the fold. It only ever scrolls UP (see
  // `scrollSectionIntoView`), and it runs after paint so the new section is measured.
  const revealColumn = () => {
    requestAnimationFrame(() => scrollSectionIntoView(colRef.current));
  };
  const pickStream = (id) => { setSelected(id); setFocusPost(null); revealColumn(); };

  // Search results live in THIS column, and every result is a way back to where the thing
  // actually lives: a post opens its own Stream focused on that post (the same bounded
  // `focus_post_id` window the Announcements index uses), a Stream is just a selection,
  // and a private message opens that conversation.
  const openSearchPost = (streamId, postId) => {
    setSelected(streamId || ALL);
    setFocusPost(postId);
    revealColumn();
    if (onClearSearch) onClearSearch();
  };
  const openSearchStream = (id) => {
    pickStream(id);
    if (onClearSearch) onClearSearch();
  };
  const openSearchThread = (id) => {
    openThread(id);
    if (onClearSearch) onClearSearch();
  };

  // Group Member Messaging V1 — the member's OWN conversations in this Group. Only threads
  // they participate in come back, and the unread aggregate is its own number: Member
  // Message unread never touches Stream unread (messaging spec §14).
  const threadsQ = useQuery({
    queryKey: ["group-threads", group.id],
    queryFn: async () => (await api.get(`/groups/${group.id}/messages/threads`)).data,
  });
  const threads = threadsQ.data?.threads || [];
  const messagesEnabled = threadsQ.data?.messages_enabled !== false;
  const canStartThread = Boolean(threadsQ.data?.can_create);
  const refreshThreads = () => qc.invalidateQueries({ queryKey: ["group-threads", group.id] });
  const openThread = (id) => {
    setSelected(`${MSG}${id}`);
    setFocusPost(null);
    revealColumn();
    refreshThreads();
    if (onThreadOpened) onThreadOpened(id);
  };
  // A fresh search submission puts the results at the top of the reading area, even if
  // the reader was deep in a feed when they pressed Enter.
  React.useEffect(() => {
    if (searchQuery) revealColumn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // A Members-tab "Message" action (or a `?thread=` deep link) selects the conversation
  // after this panel is already mounted.
  React.useEffect(() => {
    if (initialThreadId) setSelected(`${MSG}${initialThreadId}`);
  }, [initialThreadId]);

  // "Message" on a post's action row (React · Comment · Re-Chat · Message). The server
  // resolves or creates the ONE canonical 1:1 thread for this Group and this pair, so the
  // same conversation opens however the member got to it.
  const messageAuthor = async (author) => {
    if (!author?.user_id) return;
    try {
      const { data } = await api.post(`/groups/${group.id}/messages/threads`,
        { participant_ids: [author.user_id] });
      openThread(data.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open that conversation.");
    }
  };

  // The sticky strip's own height, published as `--so-pinned-h` on the feed column. Every
  // post card adds it to its `scroll-margin-top` (see `.so-pinned-aware` in index.css), so
  // jumping to a pin, a deep-linked announcement or a comment anchor always lands BELOW
  // the strip instead of underneath it. Measured, never hard-coded: the strip changes
  // height when it is collapsed and when a pin is added or removed.
  const pinnedRef = React.useRef(null);
  const colRef = React.useRef(null);
  React.useEffect(() => {
    const host = colRef.current;
    if (!host) return undefined;
    const el = pinnedRef.current;
    const apply = () => host.style.setProperty(
      "--so-pinned-h", `${el ? el.offsetHeight : 0}px`);
    apply();
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, [pins.length, selected]);

  const active = streams.filter((s) => !s.archived);
  const archived = streams.filter((s) => s.archived);
  const current = selected === ALL || selected === ANN || isThread(selected)
    ? null : streams.find((s) => s.id === selected);
  // The composer must always name a Stream: All Streams resolves to the Group Stream.
  const composeStream = current && !current.archived ? current.id : defaultId;
  // What the compact composer calls the destination ("Message #Group Stream…").
  const composeName = (streams.find((s) => s.id === composeStream) || {}).name
    || "Group Stream";

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); refresh(); }
    catch (err) { toast.error(err?.response?.data?.detail || "That didn't work."); }
  };

  // Opening a specific Stream clears ITS marker. All Streams deliberately clears nothing:
  // it is the default landing feed, so clearing there would wipe every marker before the
  // member had a chance to see where the activity actually is. The server owns the cursor;
  // the cache is patched in place so this cannot loop.
  React.useEffect(() => {
    if (!current || current.archived || !current.has_unread) return;
    const id = current.id;
    api.post(`/groups/${group.id}/streams/read`, { stream_ids: [id] }).then(() => {
      qc.setQueryData(["group-streams", group.id, showArchived], (prev) => {
        if (!prev) return prev;
        const next = prev.streams.map(
          (s) => (s.id === id ? { ...s, has_unread: false } : s));
        return { ...prev, streams: next, unread_any: next.some((s) => s.has_unread) };
      });
    }).catch(() => { /* a marker left standing is harmless */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id, current?.has_unread]);

  // Persisted order is authoritative: send the whole ACTIVE id list in its new order.
  const move = (id, delta) => {
    const ids = active.map((s) => s.id);
    const i = ids.indexOf(id);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= ids.length) return Promise.resolve();
    [ids[i], ids[j]] = [ids[j], ids[i]];
    return api.post(`/groups/${group.id}/streams/reorder`, { stream_ids: ids });
  };

  const Item = ({ id, name, icon: Icon, locked, archivedFlag, unread, count, card }) => (
    <button type="button" onClick={() => pickStream(id)}
            aria-current={selected === id ? "true" : undefined}
            data-testid={`group-stream-item-${id}`}
            className={`flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors lg:w-full ${
              selected === id ? "bg-teal-50 font-[600] text-teal-700"
                : unread ? "font-[600] text-foreground hover:bg-secondary"
                  : "text-foreground/70 hover:bg-secondary hover:text-foreground"}`}>
      {/* A 1:1 conversation is a PERSON, so the rail shows their face rather than a
          generic chat glyph — the same card the conversation header and each message
          avatar use. Streams, All Streams, Announcements and multi-member conversations
          keep their icons. */}
      {card ? (
        <Avatar className="h-5 w-5 shrink-0">
          {card.headshot_url ? <AvatarImage src={fileUrl(card.headshot_url)} alt="" /> : null}
          <AvatarFallback className="bg-teal-50 text-[9px] text-teal-700">
            {(card.display_name || "?")[0].toUpperCase()}
          </AvatarFallback>
        </Avatar>
      ) : <Icon className="h-4 w-4 shrink-0" />}
      <span className="truncate">{name}</span>
      {locked ? <Lock className="h-3 w-3 shrink-0 text-muted-foreground" /> : null}
      {unread ? (
        /* A dot, not a count: the server answers one flag per Stream from one
           aggregation, so the rail costs the same whether you have 3 Streams or 50.
           A private conversation DOES carry a count — it is its own first-class unread
           state and there are at most a handful of threads. */
        <span className="ml-auto flex shrink-0 items-center gap-1"
              data-testid={`group-stream-unread-${id}`}>
          {count ? (
            <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-teal-600 px-1.5 text-[11px] font-[680] text-white">
              {count > 99 ? "99+" : count}
            </span>
          ) : (
            <span aria-hidden="true" className="h-2 w-2 rounded-full bg-teal-600" />
          )}
          <span className="sr-only">
            {count ? `${count} unread messages` : "unread messages"}
          </span>
        </span>
      ) : null}
      {archivedFlag ? (
        <span className="shrink-0 text-[10px] uppercase tracking-wide text-muted-foreground">
          archived
        </span>
      ) : null}
    </button>
  );

  return (
    <div className="grid items-start gap-4 lg:grid-cols-[220px_minmax(0,1fr)]"
         data-testid="group-chatter-panel">
      {/* The Stream list follows the reader down the conversation (correction §2): from
          `lg` up it sticks below the sticky Group tab bar and takes its OWN scrollbar
          once there are more Streams than fit; on phones it is the sticky horizontal
          selector strip instead, which keeps Stream navigation one tap away without
          giving a 390px viewport a tall column (correction §4). */}
      <Card className={`min-w-0 border-0 p-2 lg:p-3 ${STICKY_RAIL}`}
            data-testid="group-stream-rail">
        <nav aria-label="Streams"
             className="flex gap-1 overflow-x-auto pb-1 lg:flex-col lg:overflow-x-visible lg:pb-0">
          <Item id={ALL} name="All Streams" icon={Layers}
                unread={Boolean(data?.unread_any)} />
          {/* GRP-DEC-333 — Announcements sits directly under All Streams and ABOVE the
              Stream list, separated from it, because it is a Group-wide index rather than
              one more Stream. It carries no unread dot: it owns no read cursor. */}
          <Item id={ANN} name="Announcements" icon={Megaphone} />
          <span aria-hidden="true"
                className="shrink-0 self-stretch border-l border-border lg:my-1 lg:self-auto lg:border-l-0 lg:border-t" />
          {active.map((s) => (
            <Item key={s.id} id={s.id} name={s.name} icon={Hash}
                  unread={s.has_unread}
                  locked={s.post_permission === "admins"} />
          ))}
          {archived.map((s) => (
            <Item key={s.id} id={s.id} name={s.name} icon={Hash} archivedFlag />
          ))}
          {/* Group Member Messaging V1 (spec §13) — private conversations sit UNDER the
              Streams list, visually separated, and are never intermingled with Streams.
              Only the conversations this member is in appear; the roster is not listed
              here as though a conversation already existed. */}
          {messagesEnabled || threads.length ? (
            <>
              <span aria-hidden="true"
                    className="shrink-0 self-stretch border-l border-border lg:my-1 lg:self-auto lg:border-l-0 lg:border-t" />
              <div className="flex shrink-0 items-center gap-2 px-3 py-1 lg:w-full"
                   data-testid="group-member-messages-section">
                <MessageSquare className="h-4 w-4 shrink-0 text-muted-foreground" />
                <span className="text-xs font-[640] uppercase tracking-wide text-muted-foreground">
                  Member messages
                </span>
                {threadsQ.data?.unread_total ? (
                  <span className="ml-auto inline-flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full bg-teal-600 px-1.5 text-[11px] font-[680] text-white"
                        data-testid="group-member-messages-unread">
                    {threadsQ.data.unread_total > 99 ? "99+" : threadsQ.data.unread_total}
                  </span>
                ) : null}
              </div>
              {threads.map((t) => (
                <Item key={t.id} id={`${MSG}${t.id}`} name={t.label}
                      icon={t.type === "multi" ? Users : MessageSquare}
                      card={t.type === "direct" ? t.other : null}
                      unread={t.unread > 0} count={t.unread} />
              ))}
              {canStartThread ? (
                <Button size="sm" variant="ghost"
                        className="h-7 shrink-0 justify-start px-2 text-xs lg:w-full"
                        onClick={() => setComposing(true)}
                        data-testid="group-new-message">
                  <Plus className="mr-1 h-3.5 w-3.5" />New message
                </Button>
              ) : null}
            </>
          ) : null}
        </nav>
        {canManage ? (
          <div className="mt-2 flex flex-wrap gap-1 border-t pt-2">
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                    onClick={() => setCreating(true)} data-testid="group-stream-create">
              <Plus className="mr-1 h-3.5 w-3.5" />New stream
            </Button>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                    onClick={() => setShowArchived((v) => !v)}
                    data-testid="group-stream-toggle-archived">
              {showArchived ? "Hide archived" : "Show archived"}
            </Button>
          </div>
        ) : null}
      </Card>

      <div ref={colRef} className="so-pinned-aware min-w-0 space-y-3">
        {searchQuery ? (
          <GroupSearchResults group={group} query={searchQuery}
                              onOpenPost={openSearchPost} onOpenStream={openSearchStream}
                              onOpenThread={openSearchThread} onClear={onClearSearch} />
        ) : isThread(selected) ? (
          /* The conversation REPLACES the Stream feed in this same column (spec §15) —
             the Stream header, the pinned strip and the Stream composer are all absent,
             and the member is still inside the Group shell. */
          <GroupMessageThread group={group} threadId={threadOf(selected)}
                              onClose={() => { setSelected(ALL); refreshThreads(); }}
                              onChanged={refreshThreads} />
        ) : (
        <>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <h2 className="truncate text-base font-semibold" data-testid="group-stream-title">
              {selected === ANN ? "Announcements" : (current ? current.name : "All Streams")}
            </h2>
            <p className="text-sm text-muted-foreground" data-testid="group-stream-subtitle">
              {selected === ANN
                ? "Every announcement in this group. Open one to jump to it in its stream."
                : current
                  ? (current.description
                    || (current.archived ? "Archived — read-only history."
                      : current.post_permission === "admins"
                        ? "Only organizers can post here." : "Everyone in the group can post."))
                  : "Everything from every active stream."}
            </p>
          </div>
          {canManage && current ? (
            <div className="flex flex-wrap gap-1.5">
              {!current.archived && active.length > 1 ? (
                <>
                  <Button size="sm" variant="outline" className="px-2"
                          data-testid="group-stream-move-up"
                          disabled={active.findIndex((s) => s.id === current.id) === 0}
                          onClick={() => act(() => move(current.id, -1), "Streams reordered.")}>
                    <ChevronUp className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" className="px-2"
                          data-testid="group-stream-move-down"
                          disabled={active.findIndex((s) => s.id === current.id) === active.length - 1}
                          onClick={() => act(() => move(current.id, 1), "Streams reordered.")}>
                    <ChevronDown className="h-3.5 w-3.5" />
                  </Button>
                </>
              ) : null}
              {!current.archived ? (
                <Button size="sm" variant="outline" data-testid="group-stream-rename"
                        onClick={() => setRenaming(current)}>
                  <Pencil className="mr-1.5 h-3.5 w-3.5" />Rename
                </Button>
              ) : null}
              {!current.archived ? (
                <Button size="sm" variant="outline" data-testid="group-stream-permission-toggle"
                        onClick={() => act(() => api.put(
                          `/groups/${group.id}/streams/${current.id}`,
                          { post_permission: current.post_permission === "admins" ? "all" : "admins" }),
                        "Stream posting updated.")}>
                  <Settings2 className="mr-1.5 h-3.5 w-3.5" />
                  {current.post_permission === "admins" ? "Allow all members" : "Organizers only"}
                </Button>
              ) : null}
              {!current.is_default && !current.archived ? (
                <Button size="sm" variant="ghost" data-testid="group-stream-archive"
                        onClick={() => act(() => api.post(
                          `/groups/${group.id}/streams/${current.id}/archive`),
                        "Stream archived.").then(() => setSelected(ALL))}>
                  Archive
                </Button>
              ) : null}
              {current.archived ? (
                <Button size="sm" variant="outline" data-testid="group-stream-restore"
                        onClick={() => act(() => api.post(
                          `/groups/${group.id}/streams/${current.id}/restore`),
                        "Stream restored.")}>
                  Restore
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>

        {current?.archived ? (
          <Card className="border-0 p-6 text-center" data-testid="group-stream-archived-notice">
            <p className="text-sm text-muted-foreground">
              This stream is archived. Its history is read-only.
            </p>
          </Card>
        ) : null}

        {selected === ANN ? (
          <AnnouncementsIndex groupId={group.id} onOpen={openAnnouncement} />
        ) : (
        <>
        {/* GRP-DEC-336 — the pinned strip STICKS below the Group tab bar, so a pin stays
            reachable however far down the conversation the reader is. It is opaque with a
            soft edge (nothing bleeds through it), it keeps a gap to the first message, and
            `--so-pinned-h` below tells every post how far to stay clear of it. */}
        {pins.length ? (
          <div ref={pinnedRef}
               className="sticky top-[calc(var(--so-nav-offset)+var(--so-tabbar-h))] z-[15] -mx-1 bg-background px-1 pb-3 pt-1">
            <PinnedStrip groupId={group.id} pins={pins}
                         maxPins={pinsQ.data?.max_pins || 3} canManage={canManage}
                         onOpen={openPinned} onChanged={pinsChangedFromStrip} />
          </div>
        ) : null}
        <ChatterFeed
          key={`${group.id}:${selected}:${feedKey}:${focusPost || ""}:${focusNonce}`}
          focusPostId={focusPost}
          profileType="personal"
          profileId={profileId}
          ownerName={group.display_name}
          feedUrl={`/groups/${group.id}/posts`}
          feedParams={current ? { stream_id: current.id } : {}}
          postPayload={{ group_id: group.id, stream_id: composeStream }}
          identityPicker={activeIdentity ? {
            options: identityOpts,
            value: keyOf(activeIdentity),
            onChange: setIdentityKey,
            postAs: { type: activeIdentity.identity_type, id: activeIdentity.identity_id },
          } : null}
          commentIdentity={groupIdentity}
          disableAssist
          groupCanManage={canManage}
          onPinChanged={pinsChanged}
          onMessageAuthor={canStartThread ? messageAuthor : null}
          viewerUserId={authUser?.user_id}
          groupMemberCount={group.member_count}
          /* Conversational presentation of the same engine (GRP-DEC-320/321): compact
             composer under the feed, oldest → newest. */
          conversation
          composerPlaceholder={`Message #${composeName}…`}
          emptyHint="Say something — your message starts the conversation."
        />
        </>
        )}
        </>
        )}
      </div>

      <CreateStreamDialog groupId={group.id} open={creating} onOpenChange={setCreating}
                          onCreated={refresh} />
      <RenameStreamDialog groupId={group.id} stream={renaming}
                          onOpenChange={(v) => { if (!v) setRenaming(null); }}
                          onSaved={refresh} />
      <GroupNewMessageDialog groupId={group.id} open={composing}
                             onOpenChange={setComposing} onCreated={openThread} />
    </div>
  );
}
